/**
 * #186: the daily "unlogged time" card (My Day and Proposals). It lists the person's cases with tracked time not yet
 * logged; "Log" writes one Desk time entry for the accumulated time (billable and the Phone / IT / MACD domain from
 * the case's coverage defaults) and clears it; "Discard" drops it (audited, nothing reaches Desk). Hiding the card
 * hides it until tomorrow.
 */
import { Button, cn, ErrorBanner, safeStorage, toast } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock, X } from 'lucide-react'
import * as React from 'react'
import { officeDateTimeLocal } from '../../lib/toronto-time'
import { newIdempotencyKey } from '../composer/fingerprint'
import { settlePending } from './tracked'
import { trackedApi, trackedKeys, type UnloggedTimeRow, unloggedQuery } from './tracked-api'

export const UNLOGGED_CARD_HIDDEN_KEY = 'pb.unlogged-time.hidden-on'

function today(): string {
  return officeDateTimeLocal(new Date().toISOString()).slice(0, 10)
}

export function UnloggedTimeCard({
  onOpenCase,
  big = false,
  className,
}: {
  onOpenCase?: (row: UnloggedTimeRow) => void
  /** Phone sizing (My Day). */
  big?: boolean
  className?: string
}) {
  const [hidden, setHidden] = React.useState(() => safeStorage.getItem(UNLOGGED_CARD_HIDDEN_KEY) === today())
  const q = useQuery({ ...unloggedQuery(), enabled: !hidden })
  if (hidden) return null
  if (q.isError) {
    return (
      <div className={className}>
        <ErrorBanner
          tone="warning"
          title="Unlogged time is unreachable"
          detail={(q.error as Error).message}
          onRetry={() => void q.refetch()}
        />
      </div>
    )
  }
  const rows = q.data ?? []
  if (rows.length === 0) return null
  const total = rows.reduce((n, r) => n + r.minutes, 0)
  return (
    <section
      aria-label="Unlogged time"
      className={cn('space-y-2 rounded-lg border border-primary/30 bg-primary-soft px-3 py-2', className)}
    >
      <header className="flex items-center gap-2">
        <Clock className="size-4 text-primary" />
        <h2 className="flex-1 text-sm font-medium text-primary">
          Unlogged time · {total} min on {rows.length} {rows.length === 1 ? 'case' : 'cases'}
        </h2>
        <button
          type="button"
          aria-label="Hide until tomorrow"
          title="Hide until tomorrow (the time stays tracked)"
          className="inline-flex size-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-muted"
          onClick={() => {
            safeStorage.setItem(UNLOGGED_CARD_HIDDEN_KEY, today())
            setHidden(true)
          }}
        >
          <X className="size-4" />
        </button>
      </header>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <UnloggedRow key={r.case_id} row={r} big={big} onOpenCase={onOpenCase} />
        ))}
      </ul>
    </section>
  )
}

function UnloggedRow({ row, big, onOpenCase }: { row: UnloggedTimeRow; big: boolean; onOpenCase?: (row: UnloggedTimeRow) => void }) {
  const qc = useQueryClient()
  const ref = React.useMemo(() => `unlogged:${row.case_id}:${newIdempotencyKey()}`, [row.case_id])
  const done = () => {
    settlePending(row.case_id)
    void qc.invalidateQueries({ queryKey: trackedKeys.unlogged })
    void qc.invalidateQueries({ queryKey: trackedKeys.case(row.case_id) })
  }
  const log = useMutation({
    mutationFn: () => {
      if (!row.desk_ticket_id) throw new Error('This case has no live Desk ticket; open it to log time.')
      return trackedApi().log({
        case_id: row.case_id,
        desk_ticket_id: row.desk_ticket_id,
        minutes: row.minutes,
        note: null,
        no_time: false,
        prompt_ref: ref,
        source: 'manual',
        tracked_seconds: row.seconds,
      })
    },
    onSuccess: (entry) => {
      done()
      toast.success(
        `Logged ${entry.minutes} min on ${row.case_number}${entry.desk_sync_state === 'pending_token' ? ' (reaches Desk when you reconnect)' : ''}`,
      )
    },
  })
  const discard = useMutation({
    mutationFn: () => trackedApi().discard(row.case_id),
    onSuccess: () => {
      done()
      toast(`Dropped the tracked time on ${row.case_number}. Nothing was written to Desk.`)
    },
  })
  const size = big ? 'h-11' : undefined
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm">
      <span className="min-w-0 flex-1">
        {onOpenCase ? (
          <button type="button" className="cursor-pointer font-medium hover:underline" onClick={() => onOpenCase(row)}>
            {row.case_number}
          </button>
        ) : (
          <span className="font-medium">{row.case_number}</span>
        )}{' '}
        <span className="text-muted-foreground">{row.case_title}</span>
        <span className="block text-xs text-subtle">{row.source}</span>
      </span>
      <Button
        size="sm"
        variant="primary"
        className={size}
        disabled={log.isPending || discard.isPending || !row.desk_ticket_id}
        onClick={() => log.mutate()}
      >
        Log {row.minutes} min
      </Button>
      <Button size="sm" variant="ghost" className={size} disabled={log.isPending || discard.isPending} onClick={() => discard.mutate()}>
        Discard
      </Button>
      {log.isError || discard.isError ? (
        <p role="alert" className="w-full text-xs text-destructive">
          {((log.error ?? discard.error) as Error).message}
        </p>
      ) : null}
    </li>
  )
}
